import { msToKnots } from "../lib/format.js";
import "./NavDisplay.css";

// Photo-real Airbus ARC-mode Navigation Display (ND).
// Self-contained SVG. Own aircraft fixed at bottom-centre, heading "up".

const VB = 500; // viewBox size
const CX = 250; // own-ship X (bottom centre)
const CY = 430; // own-ship Y (near bottom)
const R_OUTER = 360; // radius (px) mapped to rangeNm
const ARC_SPAN = 90; // compass arc span in degrees (±45 about heading)

const COL = {
  bg: "#05070a",
  mark: "#e8f0f5",
  green: "#12e29a",
  cyan: "#35d0ff",
  magenta: "#ff4fd8",
  yellow: "#ffe14d",
  red: "#ff3b30",
  white: "#ffffff",
};

const rad = (d) => (d * Math.PI) / 180;
const norm360 = (d) => ((d % 360) + 360) % 360;

// Point on the compass arc for a *relative* bearing (deg from own heading,
// where 0 = up/ahead). Returns {x,y} at radius R_OUTER around own ship.
function arcPoint(relDeg, r = R_OUTER) {
  const a = rad(relDeg - 90); // -90 so 0deg points up
  return { x: CX + r * Math.cos(a), y: CY + r * Math.sin(a) };
}

function isEmergency(squawk) {
  const s = String(squawk || "");
  return s === "7500" || s === "7600" || s === "7700";
}

export default function NavDisplay({ aircraft, traffic = [], rangeNm = 40 }) {
  if (!aircraft) {
    return (
      <div className="nd-root nd-stby" role="img" aria-label="Navigation display in standby">
        <svg viewBox={`0 0 ${VB} ${VB}`} className="nd-svg" preserveAspectRatio="xMidYMid meet">
          <rect x="0" y="0" width={VB} height={VB} fill={COL.bg} />
          <text x={CX} y={CY - 180} className="nd-stby-text" textAnchor="middle">ND STBY</text>
        </svg>
      </div>
    );
  }

  const hdg = norm360(aircraft.heading ?? 0);
  const olat = aircraft.latitude;
  const olon = aircraft.longitude;
  const ownAlt = aircraft.baroAltitude; // metres, may be undefined
  const gs = msToKnots(aircraft.velocity);

  const cosLat = Math.cos(rad(olat || 0));

  // Compute plotted traffic within range.
  const plotted = [];
  if (olat != null && olon != null) {
    for (const t of traffic) {
      if (t == null || t.latitude == null || t.longitude == null) continue;
      if (t.icao24 && aircraft.icao24 && t.icao24 === aircraft.icao24) continue;
      // Equirectangular approx -> nm in local ENU frame.
      const dxNm = (t.longitude - olon) * 60 * cosLat; // east
      const dyNm = (t.latitude - olat) * 60; // north
      const dist = Math.hypot(dxNm, dyNm);
      if (dist > rangeNm) continue; // clip beyond range
      // Rotate frame by -heading so "ahead" (own heading) points up.
      const h = rad(hdg);
      // Screen: up = ahead, right = right of nose.
      const east = dxNm, north = dyNm;
      const fwd = north * Math.cos(h) + east * Math.sin(h); // along nose
      const rgt = east * Math.cos(h) - north * Math.sin(h); // right of nose
      const scale = R_OUTER / rangeNm;
      const x = CX + rgt * scale;
      const y = CY - fwd * scale;

      let relAltTag = null;
      if (ownAlt != null && t.baroAltitude != null) {
        const diffFt = (t.baroAltitude - ownAlt) * 3.28084;
        const hundreds = Math.round(diffFt / 100);
        const sign = hundreds > 0 ? "+" : hundreds < 0 ? "-" : "";
        relAltTag = `${sign}${String(Math.abs(hundreds)).padStart(2, "0")}`;
      }

      plotted.push({
        key: t.icao24 || `${t.latitude},${t.longitude}`,
        x, y,
        emergency: isEmergency(t.squawk),
        relAltTag,
        above: ownAlt != null && t.baroAltitude != null && t.baroAltitude > ownAlt,
      });
    }
  }

  // Range arcs (outer = rangeNm, plus 1/2 and 1/4 rings inside the ARC).
  const arcs = [
    { r: R_OUTER, nm: rangeNm },
    { r: R_OUTER * (2 / 3), nm: Math.round((rangeNm * 2) / 3) },
    { r: R_OUTER / 3, nm: Math.round(rangeNm / 3) },
  ];
  const arcPath = (r) => {
    const a0 = arcPoint(-ARC_SPAN / 2, r);
    const a1 = arcPoint(ARC_SPAN / 2, r);
    return `M ${a0.x} ${a0.y} A ${r} ${r} 0 0 1 ${a1.x} ${a1.y}`;
  };

  // Compass ticks every 10deg across the arc; labels every 30deg.
  const ticks = [];
  for (let rel = -ARC_SPAN / 2; rel <= ARC_SPAN / 2 + 0.001; rel += 5) {
    const absDeg = norm360(hdg + rel);
    const major = Math.round(absDeg) % 10 === 0;
    const label = Math.round(absDeg) % 30 === 0;
    const outer = arcPoint(rel, R_OUTER);
    const inner = arcPoint(rel, R_OUTER - (major ? 16 : 9));
    ticks.push({ rel, absDeg, major, label, outer, inner });
  }

  const hdgStr = String(Math.round(hdg)).padStart(3, "0");
  const trafficCount = plotted.length;
  const ariaLabel = `Navigation display, heading ${hdgStr} degrees, ${trafficCount} nearby aircraft in range`;

  const trackTop = arcPoint(0, R_OUTER);

  return (
    <div className="nd-root" role="img" aria-label={ariaLabel}>
      <svg viewBox={`0 0 ${VB} ${VB}`} className="nd-svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <radialGradient id="ndGlow" cx="50%" cy="85%" r="90%">
            <stop offset="0%" stopColor="#0b141c" />
            <stop offset="70%" stopColor="#05070a" />
            <stop offset="100%" stopColor="#010203" />
          </radialGradient>
          <clipPath id="ndClip">
            <path d={`M ${CX} ${CY} L ${arcPoint(-ARC_SPAN / 2).x} ${arcPoint(-ARC_SPAN / 2).y} A ${R_OUTER} ${R_OUTER} 0 0 1 ${arcPoint(ARC_SPAN / 2).x} ${arcPoint(ARC_SPAN / 2).y} Z`} />
          </clipPath>
        </defs>

        <rect x="0" y="0" width={VB} height={VB} fill="url(#ndGlow)" />

        {/* Range arcs */}
        <g className="nd-arcs" fill="none" stroke={COL.mark} strokeWidth="1.4">
          {arcs.map((a, i) => (
            <path key={i} d={arcPath(a.r)} opacity={i === 0 ? 0.9 : 0.55}
              strokeDasharray={i === 0 ? "none" : "4 6"} />
          ))}
        </g>
        {/* Range labels along the up-line */}
        {arcs.map((a, i) => (
          <text key={`rl${i}`} x={CX + 6} y={CY - a.r + 4} className="nd-range-label"
            textAnchor="start">{a.nm}</text>
        ))}

        {/* Compass ticks + labels */}
        <g>
          {ticks.map((tk, i) => (
            <line key={`t${i}`} x1={tk.inner.x} y1={tk.inner.y} x2={tk.outer.x} y2={tk.outer.y}
              stroke={COL.mark} strokeWidth={tk.major ? 1.8 : 1} opacity={tk.major ? 0.95 : 0.6} />
          ))}
          {ticks.filter((t) => t.label).map((tk, i) => {
            const p = arcPoint(tk.rel, R_OUTER - 32);
            return (
              <text key={`l${i}`} x={p.x} y={p.y + 4}
                transform={`rotate(${tk.rel} ${p.x} ${p.y})`}
                className="nd-compass-label" textAnchor="middle">
                {String(Math.round(tk.absDeg / 10)).padStart(2, "0")}
              </text>
            );
          })}
        </g>

        {/* Track / heading line up from own ship */}
        <line x1={CX} y1={CY} x2={trackTop.x} y2={trackTop.y} stroke={COL.green}
          strokeWidth="2" opacity="0.85" className="nd-track" />

        {/* Traffic diamonds (clipped to the ARC wedge) */}
        <g clipPath="url(#ndClip)">
          {plotted.map((p) => {
            const c = p.emergency ? COL.red : COL.cyan;
            const s = 8;
            return (
              <g key={p.key} className="nd-traffic">
                <path
                  d={`M ${p.x} ${p.y - s} L ${p.x + s} ${p.y} L ${p.x} ${p.y + s} L ${p.x - s} ${p.y} Z`}
                  fill={p.emergency ? c : "none"} stroke={c} strokeWidth="2" />
                {p.relAltTag && (
                  <text x={p.x} y={p.above ? p.y - s - 3 : p.y + s + 11}
                    className="nd-alt-tag" textAnchor="middle" fill={c}>
                    {p.relAltTag}
                  </text>
                )}
              </g>
            );
          })}
        </g>

        {/* Own aircraft symbol (fixed yellow, at bottom centre) */}
        <g stroke={COL.yellow} strokeWidth="3" fill="none" strokeLinecap="round" className="nd-ownship">
          <line x1={CX} y1={CY - 14} x2={CX} y2={CY + 14} />
          <line x1={CX - 13} y1={CY} x2={CX + 13} y2={CY} />
          <line x1={CX - 8} y1={CY + 12} x2={CX + 8} y2={CY + 12} />
        </g>

        {/* Heading readout box (top centre) */}
        <g>
          <polygon points={`${CX},${CY - R_OUTER - 2} ${CX - 7},${CY - R_OUTER - 14} ${CX + 7},${CY - R_OUTER - 14}`}
            fill={COL.green} />
          <rect x={CX - 30} y="14" width="60" height="26" rx="3" fill="#04110c"
            stroke={COL.green} strokeWidth="1.4" />
          <text x={CX} y="33" className="nd-hdg-readout" textAnchor="middle" fill={COL.green}>{hdgStr}</text>
        </g>

        {/* GS / TAS readout (top-left) */}
        <g>
          <text x="14" y="26" className="nd-gs-label" fill={COL.mark}>GS</text>
          <text x="44" y="26" className="nd-gs-value" fill={COL.green}>
            {gs == null ? "---" : gs}
          </text>
          <text x="14" y="46" className="nd-gs-label" fill={COL.mark}>TAS</text>
          <text x="48" y="46" className="nd-gs-value" fill={COL.green}>
            {gs == null ? "---" : gs}
          </text>
        </g>

        {/* Mode / range legend (top-right) */}
        <g>
          <text x={VB - 14} y="26" className="nd-mode" textAnchor="end" fill={COL.magenta}>ARC</text>
          <text x={VB - 14} y="46" className="nd-mode-sub" textAnchor="end" fill={COL.cyan}>
            {rangeNm} NM
          </text>
        </g>

        {/* Callsign / traffic count (bottom-left) */}
        <g>
          <text x="14" y={VB - 30} className="nd-callsign" fill={COL.mark}>
            {(aircraft.callsign || "").trim() || "-------"}
          </text>
          <text x="14" y={VB - 12} className="nd-tfc" fill={COL.cyan}>
            TFC {trafficCount}
          </text>
        </g>
      </svg>
    </div>
  );
}
